import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2Icon, Loader2Icon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { FormField, inputClasses } from '../../components/ui/FormField';
import { buttonClasses } from '../../utils/buttonStyles';
import { track } from '../../utils/analytics';
import { sendFormEmail } from '../../utils/formMail';
import { company } from '../../data/company';
type Status = 'idle' | 'submitting' | 'success';
const quickLinks = [{
  to: '/faq',
  label: 'Frequently Asked Questions'
}, {
  to: '/warranty-claim',
  label: 'Start a warranty claim'
}, {
  to: '/order-tracking',
  label: 'Track an order'
}, {
  to: '/returns',
  label: 'Return & Refund Policy'
}, {
  to: '/shipping',
  label: 'Shipping Policy'
}, {
  to: '/warranty',
  label: 'Warranty Information'
}, {
  to: '/safety',
  label: 'Product Safety & Use'
}];
export function Support() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    order: '',
    topic: 'product',
    message: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<Status>('idle');
  const [sendError, setSendError] = useState('');
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = 'Please enter your name.';
    if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = 'Please enter a valid email address.';
    if (form.message.trim().length < 10) next.message = 'Please tell us a little more (10+ characters).';
    setErrors(next);
    if (Object.keys(next).length) return;
    setStatus('submitting');
    setSendError('');
    sendFormEmail(`New support message — ${form.topic} — ${form.name}`, {
      Name: form.name,
      Email: form.email,
      'Order number': form.order,
      Topic: form.topic,
      Message: form.message
    }, {
      replyTo: form.email
    }).then(() => {
      track('contact_submit', {
        topic: form.topic
      });
      setStatus('success');
    }).catch(() => {
      setStatus('idle');
      setSendError(`We couldn't send your message. Please try again or email ${company.supportEmail}.`);
    });
  };
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setForm(f => ({
    ...f,
    [k]: e.target.value
  }));
  return <>
      <PageHeader title="Contact & Support" lead="Professional-grade lighting deserves professional-grade support. We're here to help before and after your purchase." data-id="element-1471" data-mp="xz7trea3w9pr" />
      <div className="mx-auto grid max-w-site gap-10 px-5 py-12 md:px-8 md:py-16 lg:grid-cols-[1fr_1.4fr]" data-id="element-1472" data-mp="oyt5q75rcgik">
        <aside data-id="element-1473" data-mp="tw1pudaguumg">
          <h2 className="font-display text-2xl font-semibold uppercase" data-id="element-1474" data-mp="tvs26wcw8zjx">Get help fast</h2>
          <ul className="mt-5 divide-y divide-white/10 border-y mf-hairline" data-id="element-1475" data-mp="39nfu2dd1w1vv">
            {quickLinks.map(l => <li key={l.to} data-id="element-1476" data-mp="3eowq9vklb064">
                <Link to={l.to} className="block py-4 text-white hover:text-ice" data-id="element-1477" data-mp="366bb79zp4jp8">{l.label}</Link>
              </li>)}
          </ul>
          <h2 className="mt-8 font-display text-2xl font-semibold uppercase" data-id="element-1478" data-mp="323oh12fjymov">Reach us</h2>
          <dl className="mt-4 space-y-3 text-sm" data-id="element-1479" data-mp="3g4r4njm7451w">
            <div data-id="element-1480" data-mp="3g2elj34pbon9"><dt className="text-titanium/70" data-id="element-1481" data-mp="3g24y9nb1jvl9">Email</dt><dd data-id="element-1482" data-mp="3b54rfa6daot3"><a href="mailto:support@marineforceled.com" className="text-ice hover:underline" data-id="element-1483" data-mp="3b6fu16o2tqim">support@marineforceled.com</a></dd></div>
          </dl>
        </aside>

        <div className="rounded-2xl border mf-hairline bg-navy p-6 md:p-10" data-id="element-1484" data-mp="2igqteufa3shj">
          {status === 'success' ? <div role="status" className="py-10 text-center" data-id="element-1485" data-mp="2x652s2iuu9wx">
              <CheckCircle2Icon className="mx-auto h-10 w-10 text-marine" data-id="element-1486" data-mp="2x7pxpt92zuvb" />
              <h2 className="mt-4 font-display text-3xl font-semibold uppercase" data-id="element-1487" data-mp="2sdbyffrat3ld">Message received</h2>
              <p className="mt-2 text-titanium" data-id="element-1488" data-mp="24nrdwsfhxy9z">Thanks, {form.name.split(' ')[0]}. Our team will reply to {form.email}.</p>
            </div> : <form noValidate onSubmit={submit} className="grid gap-6 md:grid-cols-2" data-id="element-1489" data-mp="12zdyz560jawn">
              <FormField id="name" label="Name" error={errors.name} data-id="element-1490" data-mp="17xp9li44tpfx">
                <input className={inputClasses} value={form.name} onChange={set('name')} autoComplete="name" data-id="element-1491" data-mp="17vuni0h4jra9" />
              </FormField>
              <FormField id="email" label="Email" error={errors.email} data-id="element-1492" data-mp="1gq0pia1mduhn">
                <input type="email" className={inputClasses} value={form.email} onChange={set('email')} autoComplete="email" data-id="element-1493" data-mp="1gs3nbg90krwx" />
              </FormField>
              <FormField id="order" label="Order number (optional)" data-id="element-1494" data-mp="1qndqw8f297ct">
                <input className={inputClasses} value={form.order} onChange={set('order')} placeholder="#1001" data-id="element-1495" data-mp="1vnt1wtbve2rm" />
              </FormField>
              <FormField id="topic" label="Topic" data-id="element-1496" data-mp="1znts1b8c02ei">
                <select className={inputClasses} value={form.topic} onChange={set('topic')} data-id="element-1497" data-mp="19ie8o70henf">
                  <option value="product" data-id="element-1498" data-mp="5b3wyq688n5j">Product question</option>
                  <option value="sizing" data-id="element-1499" data-mp="5bdnqfz3iyca">Choosing a system</option>
                  <option value="order" data-id="element-1500" data-mp="3ty0jf7pmmyy5">Order & shipping</option>
                  <option value="warranty" data-id="element-1501" data-mp="3p68rasqtol5w">Warranty</option>
                  <option value="other" data-id="element-1502" data-mp="3p4pc75gwkg5p">Other</option>
                </select>
              </FormField>
              <div className="md:col-span-2" data-id="element-1503" data-mp="3k7fbuurz6k33">
                <FormField id="message" label="Message" error={errors.message} data-id="element-1504" data-mp="3g5tzrbn9m9ty">
                  <textarea rows={6} className={inputClasses} value={form.message} onChange={set('message')} data-id="element-1505" data-mp="gqgn47dla7ia" />
                </FormField>
              </div>
              <div className="md:col-span-2" data-id="element-1506" data-mp="bsnfd9wn15fw">
                {sendError && <p role="alert" className="mb-4 text-sm text-red-300" data-id="element-1507" data-mp="7p91opw6915m">{sendError}</p>}
                <button type="submit" disabled={status === 'submitting'} className={`${buttonClasses('primary')} w-full sm:w-auto`} data-id="element-1508" data-mp="2qnxkfdstxbw">
                  {status === 'submitting' && <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-1509" data-mp="2shavd77c6h75" />}
                  Send message
                </button>
              </div>
            </form>}
        </div>
      </div>
    </>;
}